const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const toFiniteNumber = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeLng = (lng) => {
  let normalized = Number(lng);
  while (normalized < -180) normalized += 360;
  while (normalized > 180) normalized -= 360;
  return normalized;
};

const mercatorProject = (lat, lng, zoom) => {
  const latitude = clamp(Number(lat), -85.05112878, 85.05112878);
  const longitude = normalizeLng(lng);
  const scale = 256 * Math.pow(2, Math.max(0, Number(zoom) || 0));
  const sin = Math.sin((latitude * Math.PI) / 180);
  const x = ((longitude + 180) / 360) * scale;
  const y =
    (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale;
  return { x, y };
};

export const isValidMapCoordinate = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return false;
  if (latitude === 0 && longitude === 0) return false;
  return true;
};

export const getMapViewportSnapshot = (map) => {
  if (!map) return null;
  const bounds = map.getBounds?.();
  const northEast = bounds?.getNorthEast?.();
  const southWest = bounds?.getSouthWest?.();
  if (!northEast || !southWest) return null;

  const north = toFiniteNumber(northEast.lat());
  const east = toFiniteNumber(northEast.lng());
  const south = toFiniteNumber(southWest.lat());
  const west = toFiniteNumber(southWest.lng());
  if (![north, east, south, west].every(Number.isFinite)) return null;

  return {
    zoom: Number(map.getZoom?.() || 0),
    center: {
      lat: toFiniteNumber(map.getCenter?.()?.lat?.(), null),
      lng: toFiniteNumber(map.getCenter?.()?.lng?.(), null),
    },
    bounds: { north, east, south, west },
  };
};

export const boundsToQueryParam = (bounds, paddingRatio = 0.18) => {
  const north = toFiniteNumber(bounds?.north);
  const east = toFiniteNumber(bounds?.east);
  const south = toFiniteNumber(bounds?.south);
  const west = toFiniteNumber(bounds?.west);
  if (![north, east, south, west].every(Number.isFinite)) return "";

  const latPad = Math.max((north - south) * paddingRatio, 0.015);
  const lngPad = Math.max((east - west) * paddingRatio, 0.015);
  const paddedSouth = clamp(south - latPad, -90, 90);
  const paddedNorth = clamp(north + latPad, -90, 90);
  const paddedWest = clamp(west - lngPad, -180, 180);
  const paddedEast = clamp(east + lngPad, -180, 180);
  return [paddedSouth, paddedWest, paddedNorth, paddedEast].join(",");
};

const isWithinBounds = (point, bounds) => {
  if (!bounds) return true;
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  if (!isValidMapCoordinate(lat, lng)) return false;

  const north = Number(bounds.north);
  const south = Number(bounds.south);
  const east = Number(bounds.east);
  const west = Number(bounds.west);

  if (![north, south, east, west].every(Number.isFinite)) return true;
  if (lat < south || lat > north) return false;
  if (west <= east) {
    return lng >= west && lng <= east;
  }
  return lng >= west || lng <= east;
};

const shouldClusterFleet = (visibleCount, zoom) => {
  if (visibleCount <= 0) return false;
  if (zoom <= 6) return visibleCount > 25;
  if (zoom <= 8) return visibleCount > 50;
  if (zoom <= 10) return visibleCount > 90;
  if (zoom <= 12) return visibleCount > 180;
  if (zoom <= 14) return visibleCount > 260;
  return visibleCount > 360;
};

export const buildFleetClusters = (
  points = [],
  {
    zoom = 5,
    bounds = null,
    selectedIds = new Set(),
    gridSizePx = 72,
  } = {}
) => {
  const visiblePoints = (Array.isArray(points) ? points : []).filter(
    (point) => isValidMapCoordinate(point?.lat, point?.lng) && isWithinBounds(point, bounds)
  );

  if (!shouldClusterFleet(visiblePoints.length, zoom)) {
    return {
      clusters: [],
      singles: visiblePoints,
      totalVisible: visiblePoints.length,
      isClusterMode: false,
    };
  }

  const buckets = new Map();
  const singles = [];

  visiblePoints.forEach((point) => {
    const id = String(point?.id || "");
    if (id && selectedIds.has(id)) {
      singles.push(point);
      return;
    }

    const projected = mercatorProject(point.lat, point.lng, zoom);
    const key = `${Math.floor(projected.x / gridSizePx)}:${Math.floor(projected.y / gridSizePx)}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
      existing.sumLat += Number(point.lat);
      existing.sumLng += Number(point.lng);
      existing.members.push(point);
      existing.statusCounts[point.status] = (existing.statusCounts[point.status] || 0) + 1;
      return;
    }

    buckets.set(key, {
      count: 1,
      sumLat: Number(point.lat),
      sumLng: Number(point.lng),
      members: [point],
      statusCounts: { [point.status]: 1 },
    });
  });

  const clusters = [];
  buckets.forEach((bucket, key) => {
    if (bucket.count <= 1) {
      singles.push(bucket.members[0]);
      return;
    }

    const dominantStatus =
      Object.entries(bucket.statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "inactive";

    clusters.push({
      id: `cluster:${key}`,
      lat: bucket.sumLat / bucket.count,
      lng: bucket.sumLng / bucket.count,
      count: bucket.count,
      status: dominantStatus,
      members: bucket.members,
    });
  });

  return {
    clusters,
    singles,
    totalVisible: visiblePoints.length,
    isClusterMode: clusters.length > 0,
  };
};
